<script setup lang="ts">
import { computed } from "vue";

import { cn } from "@/utils/libs/tailwind-utils";

interface IProps {
  readonly?: boolean;
  binary?: boolean;
  inputId?: string;
  ariaLabelledby?: string;
}

const props = withDefaults(defineProps<IProps>(), {
  readonly: false,
});

const emit = defineEmits(["update:modelValue"]);

const model = defineModel<boolean | null>("modelValue", {
  default: null,
});

const isChecked = computed<boolean>(() => model.value === true);
const isIndeterminate = computed<boolean>(() => model.value === null);

function toggle() {
  if (props.readonly) {
    return;
  }

  if (props.binary) {
    emit("update:modelValue", !isChecked.value);
    return;
  }

  if (isIndeterminate.value) {
    emit("update:modelValue", true);
  } else if (isChecked.value) {
    emit("update:modelValue", false);
  } else {
    emit("update:modelValue", null);
  }
}
</script>

<template>
  <div :class="cn('indeterminated-checkbox', { highlight: isChecked || isIndeterminate, checked: isChecked })">
    <input
      :aria-labelledby="props.ariaLabelledby"
      :id="props.inputId"
      :checked="isChecked"
      class="indeterminated-checkbox-input"
      :indeterminate="isIndeterminate"
      :onclick="props.readonly ? () => false : undefined"
      :readonly="props.readonly"
      type="checkbox"
      @change="toggle"
    />
    <div
      :class="
        cn('indeterminated-checkbox-box', {
          checked: isChecked,
          indeterminate: isIndeterminate,
        })
      "
      data-pc-section="box"
    >
      <svg
        v-if="isChecked"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 3L4.5 8.5L2 6"
          stroke="#006AFF"
          stroke-width="1.6666"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <svg
        v-else-if="isIndeterminate"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.5 6H9.5"
          stroke="#006AFF"
          stroke-width="1.66666"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.indeterminated-checkbox {
  @apply w-[16px] h-[16px] shrink-0 relative;

  &.highlight {
    .indeterminated-checkbox-box {
      @apply border-[#006AFF];
    }
  }

  .indeterminated-checkbox-input {
    @apply absolute top-0 left-0 w-full h-full opacity-0;
  }

  .indeterminated-checkbox-box {
    @apply absolute top-0 left-0 w-full h-full border-2 border-gray-300 rounded-[4px];
    @apply flex justify-center items-center;

    .checkbox-icon {
      @apply text-white;
    }
  }
}
</style>
